/**
 * User Onboarding Page
 * Accessed via invitation link with token
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { authService } from '../services/auth-service';
import { Button, Input, Label } from '@/components/ui';
import { TERMS_CONFIG } from '@aerotage/shared';

interface ImportMeta {
  env?: { VITE_API_URL?: string; VITE_WEBSITE_URL?: string };
}
const API_BASE = `${(import.meta as ImportMeta).env?.VITE_API_URL || '/api'}/v1`;
const WEBSITE_URL = (import.meta as ImportMeta).env?.VITE_WEBSITE_URL || 'https://aerotage.com';
const TERMS_URL = `${WEBSITE_URL}${TERMS_CONFIG.TERMS_PATH}`;
const PRIVACY_URL = `${WEBSITE_URL}${TERMS_CONFIG.PRIVACY_PATH}`;

interface Invite {
  email: string;
  role: string;
  department?: string;
}

export default function OnboardingPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState<Invite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const validateToken = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/invitations/validate/${token}`);
      setInvite(response.data.data);
      setError(null);
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError('No invitation token provided');
      setLoading(false);
    }
  }, [token, validateToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must contain uppercase, lowercase, and numbers');
      return;
    }

    if (!acceptedTerms) {
      setError('You must accept the terms of service');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API_BASE}/invitations/accept`, {
        inviteToken: token,
        password,
        name,
        termsVersion: TERMS_CONFIG.CURRENT_TERMS_VERSION,
        privacyVersion: TERMS_CONFIG.CURRENT_PRIVACY_VERSION,
      });

      // Auto-login with new credentials
      await authService.signIn(invite.email, password);

      // Redirect to dashboard
      window.location.href = '/';
    } catch (err) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Failed to complete registration');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted">
        <div className="text-center">
          <div className="text-3xl mb-4">⏳</div>
          <div>Validating invitation...</div>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted">
        <div className="bg-card p-12 rounded-xl shadow-md max-w-lg text-center">
          <div className="text-5xl mb-4">❌</div>
          <h1 className="text-destructive mb-4">Invalid Invitation</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <p className="text-muted-foreground text-sm">
            Please contact your administrator for a new invitation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 to-blue-500">
      <div className="bg-card p-12 rounded-xl shadow-2xl max-w-lg w-full m-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/aerotage-logo.svg" alt="Aerotage" className="h-12 mx-auto" />
          <h1 className="mt-4 text-foreground">Welcome to Aerotage</h1>
          <p className="text-muted-foreground text-sm">Complete your registration</p>
        </div>

        {/* Invitation Details */}
        <div className="bg-info-light border border-info rounded-lg p-4 mb-8">
          <div className="font-semibold text-info mb-2">You've been invited as:</div>
          <div className="grid gap-2 text-sm">
            <div><strong>Email:</strong> {invite.email}</div>
            <div><strong>Role:</strong> {invite.role}</div>
            {invite.department && <div><strong>Department:</strong> {invite.department}</div>}
          </div>
        </div>

        {error && (
          <div className="bg-destructive-light border border-destructive text-destructive p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <Label className="block font-semibold mb-2">
              Full Name *
            </Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-12"
              placeholder="John Doe"
            />
          </div>

          <div className="mb-6">
            <Label className="block font-semibold mb-2">
              Choose Password *
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="h-12"
              placeholder="••••••••"
            />
            <div className="mt-2 text-xs text-muted-foreground">
              <div>✓ At least 8 characters</div>
              <div>✓ Include uppercase and lowercase</div>
              <div>✓ Include numbers</div>
            </div>
          </div>

          <div className="mb-6">
            <Label className="block font-semibold mb-2">
              Confirm Password *
            </Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="h-12"
              placeholder="••••••••"
            />
          </div>

          <div className="mb-6">
            <Label className="flex items-start gap-2 cursor-pointer">
              {/* eslint-disable-next-line no-restricted-syntax -- checkbox inputs require native element */}
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
                className="mt-1"
              />
              <span className="text-sm text-foreground">
                I have read and accept the{' '}
                <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-link hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-link hover:underline">Privacy Policy</a>
              </span>
            </Label>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-base font-semibold"
          >
            {submitting ? 'Creating Account...' : 'Complete Registration'}
          </Button>
        </form>

        <div className="mt-8 text-center text-xs text-muted-foreground">
          <p>Questions? Contact admin@aerotage.com</p>
        </div>
      </div>
    </div>
  );
}

